import { ensure } from '../types/ensure';

import { AccessToken } from './AccessToken';

export interface AccessTokenProvider {
  getAccessToken: GetAccessTokenFunction;
}

type GetAccessTokenFunction = () => Promise<AccessToken>;

let browserAccessTokenProvider: AccessTokenProvider | null = null;

export const registerBrowserAccessTokenProvider = (provider: AccessTokenProvider): void => {
  browserAccessTokenProvider = provider;
};

export const resolveAccessTokenProvider = (): AccessTokenProvider => {
  const isBrowser = typeof window !== 'undefined';

  if (isBrowser) {
    ensure(
      browserAccessTokenProvider,
      'No browser access token provider registered. Call registerBrowserAccessTokenProvider first.'
    );

    return browserAccessTokenProvider;
  } else {
    return clientCredentialsFlowAccessTokenProvider;
  }
};

const ACCESS_TOKEN_RENEWAL_OFFSET_MS = 10 * 1000;

let cachedAccessToken: AccessToken | null = null;
let pendingAccessToken: Promise<AccessToken> | null = null;

const clientCredentialsFlowAccessTokenProvider: AccessTokenProvider = {
  getAccessToken: async (): Promise<AccessToken> => {
    if (cachedAccessToken && cachedAccessToken.timeToExpirationMs > ACCESS_TOKEN_RENEWAL_OFFSET_MS) {
      return cachedAccessToken;
    }

    if (!pendingAccessToken) {
      pendingAccessToken = performClientCredentialsFlow()
        .then(accessToken => (cachedAccessToken = new AccessToken(accessToken)))
        .finally(() => (pendingAccessToken = null));
    }

    return pendingAccessToken;
  },
};

const performClientCredentialsFlow = async (): Promise<string> => {
  const clientId = process.env.CLIENT_CREDENTIALS_FLOW_CLIENT_ID;
  ensure(clientId, 'Client ID is missing in the client credentials flow.');

  const clientSecret = process.env.CLIENT_CREDENTIALS_FLOW_CLIENT_SECRET;
  ensure(clientSecret, 'Client secret is missing in the client credentials flow.');

  const scope = process.env.CLIENT_CREDENTIALS_FLOW_SCOPE;
  ensure(scope, 'Scope is missing in the client credentials flow.');

  const tokenEndpoint = process.env.B2C_TOKEN_ENDPOINT;
  ensure(tokenEndpoint, 'Token endpoint is missing in the client credentials flow.');

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      client_id: clientId,
      scope: scope,
      client_secret: clientSecret,
      grant_type: 'client_credentials',
    }),
  });

  const data: ClientCredentialsFlowResponse = await response.json();

  const accessToken = data.access_token;
  ensure(accessToken, 'Access token is missing in the client credentials flow.');

  return accessToken;
};

interface ClientCredentialsFlowResponse {
  access_token: string;
  token_type: string; // 'Bearer'
  not_before: number;
  expires_in: number;
  expires_on: number;
  resource: string;
}
